import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Warehouse } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { Pagination } from '@/components/tables/Pagination';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { stockApi } from '@/services/api/endpoints/assets';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { EmployeePicker, type PickedEmployee } from '@/features/hr/EmployeePicker';
import { PERMISSIONS } from '@/types/permissions';
import {
  STOCK_MOVEMENT_TYPES,
  type StockItem,
  type StockItemInput,
  type StockMovement,
  type StockMovementInput,
  type StockMovementType,
} from '@/types/asset';
import { formatDateTime, orDash } from '@/utils/format';
import { Badge, RecordStatusBadge, STOCK_MOVEMENT_LABEL, StockLevelBadge } from './shared';

/** Local to this screen: the shared query-key registry has no entry for the lookups. */
const STOCK_LOOKUPS_KEY = ['stock-lookups'] as const;

const MOVEMENT_HINT: Record<StockMovementType, string> = {
  RECEIVE: 'Stock arriving on the shelf — a delivery or a return.',
  ISSUE: 'Stock handed out. The server refuses to issue more than is on hand.',
  ADJUST: 'A stock-take correction. It needs a direction and a reason.',
};

/**
 * Consumables — counted in quantities, not tagged one by one.
 *
 * <p>Searching, the category filter and "low stock only" all run on the
 * server; the page shows one page of items. The reorder badge is the server's
 * `belowReorderLevel` flag, never a comparison done here.
 *
 * <p>The quantity on hand changes only through movements: the add and edit
 * forms never carry it, and a new item starts at zero until stock is
 * received. Over-issue and a reasonless adjustment are refused by the server,
 * and its message is shown as it came.
 */
export function StockPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('name,asc');
  const manages = can(PERMISSIONS.STOCK_MANAGE);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowOnly, setLowOnly] = useState(false);

  // ---- add / edit
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StockItem | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [unit, setUnit] = useState('');
  const [reorderLevel, setReorderLevel] = useState('');

  // ---- retire / restore
  const [toggling, setToggling] = useState<StockItem | null>(null);

  // ---- movements
  const [moving, setMoving] = useState<StockItem | null>(null);
  const [movementType, setMovementType] = useState<StockMovementType>('RECEIVE');
  const [quantity, setQuantity] = useState('');
  const [increase, setIncrease] = useState<boolean | null>(null);
  const [issuedTo, setIssuedTo] = useState<PickedEmployee | null>(null);
  const [reference, setReference] = useState('');
  const [movementNotes, setMovementNotes] = useState('');
  const [confirmingMove, setConfirmingMove] = useState(false);

  // ---- ledger
  const [ledgerItem, setLedgerItem] = useState<StockItem | null>(null);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerSize, setLedgerSize] = useState(25);

  // The pickers come from the stock module's own lookups, readable with
  // STOCK_VIEW alone — the asset register's lists would need ASSET_VIEW.
  const lookups = useQuery({
    queryKey: STOCK_LOOKUPS_KEY,
    queryFn: () => stockApi.lookups(),
  });

  // The server already sends active consumable categories and active
  // locations only. An item being edited may sit under one since retired, so
  // its current value is kept selectable from the item itself.
  const lookupCategories = lookups.data?.categories ?? [];
  const lookupLocations = lookups.data?.locations ?? [];
  const retiredCategory =
    editing !== null && !lookupCategories.some((row) => row.id === editing.categoryId)
      ? { id: editing.categoryId, name: editing.categoryName ?? 'Current category' }
      : null;
  const retiredLocation =
    editing?.locationId !== undefined && !lookupLocations.some((row) => row.id === editing.locationId)
      ? { id: editing.locationId, name: editing.locationName ?? 'Current location' }
      : null;
  const categoryOptions: { id: string; name: string }[] =
    retiredCategory === null ? lookupCategories : [retiredCategory, ...lookupCategories];
  const locationOptions: { id: string; name: string }[] =
    retiredLocation === null ? lookupLocations : [retiredLocation, ...lookupLocations];

  const params = {
    ...table.params,
    ...(categoryFilter === '' ? {} : { categoryId: categoryFilter }),
    ...(lowOnly ? { lowOnly: true } : {}),
  };

  const items = useQuery({
    queryKey: queryKeys.stock.list(params),
    queryFn: () => stockApi.list(params),
  });

  const ledgerParams = { page: ledgerPage, size: ledgerSize };
  const ledger = useQuery({
    queryKey: queryKeys.stock.movements(ledgerItem?.id ?? '', ledgerParams),
    queryFn: () => stockApi.movements(ledgerItem?.id ?? '', ledgerParams),
    enabled: ledgerItem !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.dashboard });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assetReference.categories });
  };

  function openForm(item: StockItem | null) {
    setEditing(item);
    setAdding(item === null);
    setCode(item?.code ?? '');
    setName(item?.name ?? '');
    setCategoryId(item?.categoryId ?? '');
    setLocationId(item?.locationId ?? '');
    setUnit(item?.unit ?? '');
    setReorderLevel(item === null ? '' : String(item.reorderLevel));
  }

  function closeForm() {
    setAdding(false);
    setEditing(null);
  }

  function openMove(item: StockItem) {
    setMoving(item);
    setMovementType('RECEIVE');
    setQuantity('');
    setIncrease(null);
    setIssuedTo(null);
    setReference('');
    setMovementNotes('');
    setConfirmingMove(false);
  }

  function closeMove() {
    setMoving(null);
    setConfirmingMove(false);
  }

  function openLedger(item: StockItem) {
    setLedgerItem(item);
    setLedgerPage(0);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: StockItemInput = {
        code: code.trim(),
        name: name.trim(),
        categoryId,
        locationId: locationId === '' ? undefined : locationId,
        unit: unit.trim(),
        reorderLevel: Number(reorderLevel),
      };
      return editing === null ? stockApi.create(payload) : stockApi.update(editing.id, payload);
    },
    onSuccess: (item) => {
      toast.success(editing === null ? `${item.name} added — receive stock to fill it` : `${item.name} saved`);
      closeForm();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (item: StockItem) =>
      stockApi.setStatus(item.id, item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: (item) => {
      toast.success(item.status === 'ACTIVE' ? `${item.name} restored` : `${item.name} retired`);
      setToggling(null);
      invalidate();
    },
    onError: (error) => {
      setToggling(null);
      handleApiError(error);
    },
  });

  const move = useMutation({
    mutationFn: (item: StockItem) => {
      const payload: StockMovementInput = {
        movementType,
        quantity: Number(quantity),
        increase: movementType === 'ADJUST' ? increase ?? undefined : undefined,
        issuedToEmployeeId: movementType === 'ISSUE' ? issuedTo?.id : undefined,
        reference: reference.trim() === '' ? undefined : reference.trim(),
        notes: movementNotes.trim() === '' ? undefined : movementNotes.trim(),
      };
      return stockApi.move(item.id, payload);
    },
    onSuccess: (item) => {
      toast.success(`${item.name}: ${item.quantityOnHand} ${item.unit} on hand`);
      closeMove();
      invalidate();
    },
    onError: (error) => {
      setConfirmingMove(false);
      handleApiError(error);
    },
  });

  const formIncomplete =
    code.trim() === '' ||
    name.trim() === '' ||
    categoryId === '' ||
    unit.trim() === '' ||
    reorderLevel.trim() === '';

  const moveIncomplete =
    quantity.trim() === '' ||
    (movementType === 'ADJUST' && (increase === null || movementNotes.trim() === ''));

  const columns: Column<StockItem>[] = [
    {
      key: 'item',
      header: 'Item',
      sortKey: 'name',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.categoryName)}</span>
          <span className="block text-[11px] text-subtle">{row.locationName ?? 'No location'}</span>
        </span>
      ),
    },
    {
      key: 'onHand',
      header: 'On hand',
      sortKey: 'quantityOnHand',
      className: 'text-right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">
          {row.quantityOnHand} {row.unit}
        </span>
      ),
    },
    {
      key: 'reorder',
      header: 'Reorder at',
      className: 'text-right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums text-muted">
          {row.reorderLevel} {row.unit}
        </span>
      ),
    },
    {
      key: 'level',
      header: 'Level',
      render: (row) => <StockLevelBadge belowReorderLevel={row.belowReorderLevel} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <RecordStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => (
        <span className="flex flex-wrap justify-end gap-1.5">
          {manages && row.status === 'ACTIVE' && (
            <Button size="sm" onClick={() => openMove(row)}>
              Move stock
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => openLedger(row)}>
            Ledger
          </Button>
          {manages && (
            <>
              <Button size="sm" variant="secondary" onClick={() => openForm(row)}>
                Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setToggling(row)}>
                {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
              </Button>
            </>
          )}
        </span>
      ),
    },
  ];

  const ledgerColumns: Column<StockMovement>[] = [
    {
      key: 'type',
      header: 'Movement',
      render: (row) => (
        <Badge
          tone={
            row.movementType === 'RECEIVE' ? 'success' : row.movementType === 'ISSUE' ? 'info' : 'warning'
          }
        >
          {STOCK_MOVEMENT_LABEL[row.movementType]}
          {row.movementType === 'ADJUST' && row.increase !== undefined
            ? row.increase
              ? ' up'
              : ' down'
            : ''}
        </Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      className: 'text-right',
      render: (row) => <span className="whitespace-nowrap tabular-nums">{row.quantity}</span>,
    },
    {
      key: 'balance',
      header: 'Balance after',
      className: 'text-right',
      render: (row) => (
        <span className="whitespace-nowrap font-semibold tabular-nums">{row.balanceAfter}</span>
      ),
    },
    {
      key: 'issuedTo',
      header: 'Issued to',
      render: (row) => orDash(row.issuedToEmployeeName),
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (row) => <span className="font-mono text-[12px]">{orDash(row.reference)}</span>,
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (row) => <span className="text-[13px] text-muted">{orDash(row.notes)}</span>,
    },
    {
      key: 'time',
      header: 'When',
      render: (row) => <span className="whitespace-nowrap text-[13px]">{formatDateTime(row.occurredAt)}</span>,
    },
  ];

  const filtering = table.searchInput !== '' || categoryFilter !== '' || lowOnly;

  return (
    <>
      <ListPage
        title="Stock"
        description="Consumables counted in quantities. Quantities change only through receive, issue and adjust."
        icon={<Warehouse className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? <Button onClick={() => openForm(null)}>Add stock item</Button> : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Code or name…',
        }}
        filters={
          <>
            <FilterSelect
              label="Category"
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(value);
                table.setPage(0);
              }}
              options={lookupCategories.map((row) => ({ value: row.id, label: row.name }))}
            />
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={lowOnly}
                onChange={(event) => {
                  setLowOnly(event.target.checked);
                  table.setPage(0);
                }}
                className="h-4 w-4 border-line"
              />
              Low stock only
            </label>
          </>
        }
        pagination={items.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={items.data?.data}
          rowKey={(row) => row.id}
          loading={items.isPending}
          error={items.error}
          onRetry={() => void items.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={filtering ? 'Nothing matched' : 'No stock items yet'}
          emptyDescription={
            filtering
              ? lowOnly
                ? 'Nothing is at or below its reorder level with these filters.'
                : 'The search matches code and name.'
              : 'Add a stock item under a consumable category, then receive stock into it.'
          }
        />
      </ListPage>

      {/* ---- add / edit ------------------------------------------------ */}
      <Modal
        open={adding || editing !== null}
        onClose={closeForm}
        title={editing === null ? 'Add stock item' : `Edit ${editing.name}`}
        description={
          editing === null
            ? 'A new item starts at zero. Receive stock to put some on the shelf.'
            : 'Details only — the quantity changes through movements.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={formIncomplete}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Code"
              value={code}
              maxLength={40}
              onChange={(event) => setCode(event.target.value)}
              required
            />
            <TextField
              label="Name"
              value={name}
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>
          <SelectField
            label="Category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
            hint={
              lookups.isError
                ? 'Categories could not be loaded.'
                : lookupCategories.length === 0 && !lookups.isPending
                  ? 'No consumable categories yet — add one in Asset Setup.'
                  : 'Consumable categories only.'
            }
          >
            <option value="">{lookups.isPending ? 'Loading…' : 'Choose a category'}</option>
            {categoryOptions.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Unit"
              value={unit}
              maxLength={30}
              placeholder="box, ream, litre…"
              onChange={(event) => setUnit(event.target.value)}
              required
            />
            <TextField
              label="Reorder level"
              type="number"
              min={0}
              step="0.01"
              value={reorderLevel}
              onChange={(event) => setReorderLevel(event.target.value)}
              required
              hint="Flagged for reorder at or below this."
            />
          </div>
          <SelectField
            label="Location"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            hint={lookups.isError ? 'Locations could not be loaded.' : undefined}
          >
            <option value="">{lookups.isPending ? 'Loading…' : 'No location'}</option>
            {locationOptions.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </SelectField>
        </div>
      </Modal>

      {/* ---- retire / restore ------------------------------------------ */}
      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.status === 'ACTIVE' ? `Retire ${toggling.name}?` : `Restore ${toggling?.name ?? ''}?`}
        message={
          toggling?.status === 'ACTIVE'
            ? 'A retired item takes no further movements. Its ledger stays on record and it can be restored later.'
            : 'The item will accept movements again.'
        }
        confirmLabel={toggling?.status === 'ACTIVE' ? 'Retire' : 'Restore'}
        destructive={toggling?.status === 'ACTIVE'}
        loading={setStatus.isPending}
        onConfirm={() => {
          if (toggling !== null) {
            setStatus.mutate(toggling);
          }
        }}
        onCancel={() => setToggling(null)}
      />

      {/* ---- move stock ------------------------------------------------ */}
      <Modal
        open={moving !== null && !confirmingMove}
        onClose={closeMove}
        title={moving === null ? '' : `Move stock — ${moving.name}`}
        description={
          moving === null ? undefined : `On hand: ${moving.quantityOnHand} ${moving.unit}`
        }
        footer={
          <>
            <Button variant="secondary" onClick={closeMove}>
              Cancel
            </Button>
            <Button disabled={moveIncomplete} onClick={() => setConfirmingMove(true)}>
              Record {STOCK_MOVEMENT_LABEL[movementType].toLowerCase()}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div role="group" aria-label="Movement type" className="grid grid-cols-3 gap-2">
            {STOCK_MOVEMENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setMovementType(type)}
                aria-pressed={movementType === type}
                className={`border px-3 py-2 text-sm font-semibold ${
                  movementType === type
                    ? 'border-accent bg-accent-soft text-accent-soft-fg'
                    : 'border-line bg-bg text-muted'
                }`}
              >
                {type === 'RECEIVE' ? 'Receive' : type === 'ISSUE' ? 'Issue' : 'Adjust'}
              </button>
            ))}
          </div>
          <p className="text-[12px] text-muted">{MOVEMENT_HINT[movementType]}</p>

          {movementType === 'ADJUST' && (
            <div role="group" aria-label="Adjustment direction" className="grid grid-cols-2 gap-2">
              {[
                { value: true, label: 'Increase' },
                { value: false, label: 'Decrease' },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => setIncrease(option.value)}
                  aria-pressed={increase === option.value}
                  className={`border px-3 py-2 text-sm font-semibold ${
                    increase === option.value
                      ? 'border-accent bg-accent-soft text-accent-soft-fg'
                      : 'border-line bg-bg text-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <TextField
            label={moving === null ? 'Quantity' : `Quantity (${moving.unit})`}
            type="number"
            min={0.01}
            step="0.01"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            required
          />

          {movementType === 'ISSUE' && (
            <EmployeePicker
              value={issuedTo}
              onChange={setIssuedTo}
              label="Issued to"
              required={false}
            />
          )}

          <TextField
            label="Reference"
            value={reference}
            maxLength={100}
            placeholder="Delivery note, requisition…"
            onChange={(event) => setReference(event.target.value)}
          />
          <TextField
            label={movementType === 'ADJUST' ? 'Reason' : 'Notes'}
            value={movementNotes}
            maxLength={500}
            onChange={(event) => setMovementNotes(event.target.value)}
            required={movementType === 'ADJUST'}
            hint={movementType === 'ADJUST' ? 'Required — a stock-take correction needs its reason.' : undefined}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={moving !== null && confirmingMove}
        title={`Record this ${STOCK_MOVEMENT_LABEL[movementType].toLowerCase()} movement?`}
        message={
          moving === null
            ? ''
            : `${
                movementType === 'RECEIVE'
                  ? 'Receive'
                  : movementType === 'ISSUE'
                    ? 'Issue'
                    : increase === true
                      ? 'Adjust up by'
                      : 'Adjust down by'
              } ${quantity} ${moving.unit} of ${moving.name}${
                movementType === 'ISSUE' && issuedTo !== null ? ` to ${issuedTo.name}` : ''
              }. Movements are kept on the ledger and cannot be edited.`
        }
        confirmLabel="Record"
        loading={move.isPending}
        onConfirm={() => {
          if (moving !== null) {
            move.mutate(moving);
          }
        }}
        onCancel={() => setConfirmingMove(false)}
      />

      {/* ---- ledger ---------------------------------------------------- */}
      <Modal
        open={ledgerItem !== null}
        onClose={() => setLedgerItem(null)}
        title={ledgerItem === null ? '' : `Ledger — ${ledgerItem.name}`}
        description="Every movement, newest first. Balances are as the server recorded them."
        size="lg"
      >
        <div className="-mx-5 -my-4">
          <DataTable
            columns={ledgerColumns}
            rows={ledger.data?.data}
            rowKey={(row) => row.id}
            loading={ledger.isPending}
            error={ledger.error}
            onRetry={() => void ledger.refetch()}
            emptyTitle="No movements yet"
            emptyDescription="Receive stock to start this item's ledger."
          />
          <Pagination
            pagination={ledger.data?.pagination}
            onPageChange={setLedgerPage}
            onSizeChange={(size) => {
              setLedgerSize(size);
              setLedgerPage(0);
            }}
          />
        </div>
      </Modal>
    </>
  );
}
